import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  EventEmitter,
  Injectable,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  effect,
  inject,
  model,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import {
  AssetLocation,
  AssetImages,
  AssetTypeMapping,
  AssetUpdateRequest,
  ENVIRONMENT,
  FirmwareState,
  GetAssetResponse,
  assetTypeMappingToString,
  assetTypesSupportDeploymentDate,
  interchangeableTypeMappings,
} from '@assethub/shared/models';
import {
  AssetService,
  AssetStateService,
  ConfirmationService,
  DeviceAlreadyExistsError,
  ImageService,
  ProductDetails,
  ProductService,
  ProfilePictureService,
} from '@assethub/shared/services';
import { Logger } from '@assethub/shared/utils';
import { validateIsVendorSick } from '@assethub/shared/utils/validators/vendor-validation';
import { isVersionValid } from '@assethub/shared/utils/validators/version-validation';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService, SelectItem, SharedModule } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { Subscription, finalize, of, switchMap, tap } from 'rxjs';
import { CompleteUrlPipe } from '../../pipes/complete-url.pipe';
import { LocalizedDatePipe } from '../../pipes/localized-date.pipe';
import { SafeHtmlPipe } from '../../pipes/safe-html.pipe';
import { AssetLocationEditComponent } from '../asset-location-edit/asset-location-edit.component';
import { AssetLocationLabelComponent } from '../asset-location-label/asset-location-label.component';
import { AssetOptionMenuComponent } from '../asset-option-menu/asset-option-menu.component';
import { CalloutComponent } from '../callout/callout.component';
import { ConnectionStateComponent } from '../connection-state/connection-state.component';
import { DateFieldComponent } from '../date-field/date-field.component';
import { FirmwareStateIconComponent } from '../firmware-state-icon/firmware-state-icon.component';
import { AdministratorsModalComponent } from '../modals/administrators-modal/administrators-modal.component';
import { AcceptCancelOptionsComponent } from '../save-cancel-options/save-cancel-options.component';
import { SystemStateComponent } from '../system-state/system-state.component';
import { TextFieldComponent } from '../text-field/text-field.component';
import { MediaViewerComponent } from '../media-viewer/media-viewer.component';
import { MediaGalleryItem } from '../media-gallery/media-gallery.component';

@Injectable({ providedIn: 'root' })
export class AssetDetailHeaderService {
  readonly statusBar = signal<TemplateRef<any> | undefined>(undefined);
  readonly optionMenu = signal<TemplateRef<any> | undefined>(undefined);
}

@UntilDestroy()
@Component({
  selector: 'app-asset-detail-header',
  templateUrl: './asset-detail-header.component.html',
  styleUrls: ['./asset-detail-header.component.scss'],
  imports: [
    DialogModule,
    ProgressSpinnerModule,
    NgStyle,
    ButtonModule,
    TextFieldComponent,
    FormsModule,
    NgTemplateOutlet,
    AssetOptionMenuComponent,
    NgClass,
    SelectModule,
    SharedModule,
    FirmwareStateIconComponent,
    DateFieldComponent,
    CalloutComponent,
    AcceptCancelOptionsComponent,
    TranslateModule,
    CompleteUrlPipe,
    LocalizedDatePipe,
    SafeHtmlPipe,
    ConnectionStateComponent,
    SystemStateComponent,
    MediaViewerComponent,
    AssetLocationLabelComponent,
    AssetLocationEditComponent,
  ],
})
export class AssetDetailHeaderComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  asset: GetAssetResponse;

  @Output()
  assetChange: EventEmitter<GetAssetResponse> = new EventEmitter();

  readonly statusBar = this.assetDetailHeaderService.statusBar;
  readonly optionMenu = this.assetDetailHeaderService.optionMenu;
  readonly editAsset = this.assetService.editMode;
  readonly showViewer = model(false);
  readonly viewerItems = signal<MediaGalleryItem[]>([]);

  profilePicture: AssetImages = { ready: false };
  defaultPictureUrl = '';

  title = '';
  defaultTitle = '';
  vendor = '';
  serialNumber = '';
  partNumber = '';
  productName = '';
  shipImo = '';
  typeSelected: AssetTypeMapping | undefined;
  currentTypeAsString = '';
  firmwareVersion = '';
  firmwareState: FirmwareState = 'UNKNOWN';
  readonly firmwareStateOutdated: FirmwareState = 'UPDATE_AVAILABLE';
  deploymentDateTimestamp?: number;
  deploymentDate?: Date;
  shopPath = '';
  permissions?: string;

  deploymentDateLanguageKey = 'default';

  assetTypes: SelectItem[] = this.createAssetTypeList();
  assetTypeChangeable = false;
  showDeploymentDateField = false;

  phaseOutWarningMsg = '';
  successor: ProductDetails['successor'];

  updating = false;

  readonly types = AssetTypeMapping;

  firmwareVersionValid = true;
  showVersionErrorIcon = false;
  displayInvalidFirmwareDialog = false;

  vendorUpdatable = false;
  serialNumberUpdatable = false;
  partNumberUpdatable = false;
  productNameUpdatable = false;
  envFeatures = inject(ENVIRONMENT).envSpecificFeatures;

  private profilePictureSubscription?: Subscription = undefined;
  private productDetailSubscription?: Subscription = undefined;
  private location?: AssetLocation | null;

  private logger = new Logger(this.constructor.name);

  constructor(
    private assetService: AssetService,
    private profilePictureService: ProfilePictureService,
    private router: Router,
    private confirmationService: ConfirmationService,
    private langService: TranslateService,
    private messageService: MessageService,
    private sanitizer: DomSanitizer,
    private imageService: ImageService,
    private productService: ProductService,
    private completeUrlPipe: CompleteUrlPipe,
    private assetStateService: AssetStateService,
    private assetDetailHeaderService: AssetDetailHeaderService,
  ) {
    effect(() => {
      if (!this.editAsset()) {
        this.reset();
      }
    });
  }

  showVersionHelp = () => {
    this.displayInvalidFirmwareDialog = true;
  };

  ngOnInit(): void {
    if (!this.asset) {
      throw new Error('Asset property not set');
    }

    this.langService.onLangChange.pipe(untilDestroyed(this)).subscribe(() => {
      this.updateAssetAttributes(this.asset);
    });

    this.profilePictureService.profilePictureChanged.pipe(untilDestroyed(this)).subscribe(event => {
      if (!event || event.assetUuid !== this.asset.uuid) {
        return;
      }
      this.profilePicture = event.picture;
      const pic = this.profilePicture;
      if (!pic.zoomed && pic.uuid) {
        this.viewerItems.set([
          {
            type: 'custom-image',
            id: pic.uuid,
            filename: this.asset.images?.find(x => x.uuid === pic.uuid)?.name,
          },
        ]);
        this.imageService.proxyImageObservable(pic.uuid).subscribe(url => {
          pic.zoomed = url;
        });
      } else {
        this.viewerItems.set([
          {
            type: 'image',
            id: 'dummy',
            previewUrl: this.completeUrlPipe.transform(pic.normal || '', ''),
            url: this.completeUrlPipe.transform(pic.zoomed || '', ''),
          },
        ]);
      }
    });
  }

  ngOnChanges(change: SimpleChanges): void {
    if (change.asset?.currentValue) {
      this.updateAsset(change.asset.currentValue);
    }
  }

  ngOnDestroy(): void {
    if (this.profilePictureSubscription) {
      this.profilePictureSubscription.unsubscribe();
      this.profilePictureSubscription = undefined;
    }
    if (this.productDetailSubscription) {
      this.productDetailSubscription.unsubscribe();
      this.productDetailSubscription = undefined;
    }
  }

  setLocation(location: AssetLocation | undefined | null) {
    this.location = location;
  }

  navigateToMap(): void {
    this.router.navigate(['/asset', this.asset.uuid, 'map']);
  }

  openPicture(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.showViewer.set(true);
  }

  onAssetChanged(asset: GetAssetResponse): void {
    this.updateAsset(asset);
  }

  cancelEdit(): void {
    this.editAsset.set(false);
  }

  saveChangesToAsset(): void {
    const assetUpdate: AssetUpdateRequest = {};

    const newTitle = this.title.trim();
    if (newTitle !== this.asset.customName) {
      assetUpdate.customName = newTitle;
    }

    if (this.typeSelected !== this.asset.typeId) {
      assetUpdate.typeId = this.typeSelected;
    }

    this.deploymentDateTimestamp = this.deploymentDate
      ? Math.floor(this.deploymentDate.getTime() / 1000)
      : undefined;

    if (this.deploymentDateTimestamp !== this.asset.deploymentDate) {
      assetUpdate.deploymentDate = this.deploymentDateTimestamp
        ? this.deploymentDateTimestamp
        : null;
    }

    if (!this.assetTypeChangeable) {
      if (!this.typeSelected) {
        throw Error('Type property not set.');
      }

      if (this.isProduct() && this.asset.details) {
        if (this.firmwareVersion.trim() !== this.asset.details.firmwareVersion) {
          if (!this.firmwareVersionValid) {
            this.messageService.add({
              severity: 'error',
              summary: 'toasts.asset.invalid-firmware-error',
              life: 5000,
            });
            return;
          }
          assetUpdate.firmwareVersion = this.firmwareVersion.trim();
        }

        const newPartNumber = this.partNumber.trim();
        if (newPartNumber !== this.asset.details.partNumber) {
          assetUpdate.partNumber = newPartNumber;
        }

        const newSerialNumber = this.serialNumber.trim();
        if (newSerialNumber !== this.asset.details.serialNumber) {
          assetUpdate.serialNumber = newSerialNumber;
        }

        const newProductName = this.productName.trim();
        if (newProductName !== this.asset.details.productName) {
          assetUpdate.productName = newProductName;
        }

        const newVendor = this.vendor.trim();
        if (newVendor !== this.asset.details.vendor) {
          assetUpdate.vendor = newVendor;
        }
      } else if (this.isTypeShip() && typeof this.asset.shipDetails !== 'undefined') {
        const newImo = this.shipImo.trim();
        if (newImo !== this.asset.shipDetails.imo) {
          assetUpdate.imo = newImo;
        }
      }
    }

    if (!this.isTypeShip() && this.location !== undefined) {
      assetUpdate.location = this.location;
    }

    if (Object.keys(assetUpdate).length === 0) {
      this.reset();
      return;
    }

    this.updating = true;
    this.assetService.updateAsset(assetUpdate, this.asset.uuid).subscribe({
      next: result => {
        Object.assign(this.asset, result);
        this.updating = false;
        this.editAsset.set(false);
        // this.leaveEditMode();
        // this.updateAsset(result);
        this.assetChange.emit(result);
      },
      error: error => {
        if (error instanceof DeviceAlreadyExistsError) {
          this.confirmationService.confirm({
            translationKey: 'assetDetails.duplicateError',
            accept: () => this.router.navigate(['asset', error.conflictingDeviceUuid]),
          });
        } else {
          this.messageService.add({
            severity: 'error',
            sticky: true,
            summary: 'toasts.asset.update-error',
          });
          this.logger.error(error);
        }
        this.updating = false;
        // Revert state
        this.reset();
      },
    });
  }

  canShowShopLink(): boolean {
    if (!this.asset.details?.partNumber) {
      return false;
    }

    return this.asset.details.partNumber.length > 4 && this.isVendorSICK();
  }

  canShowLocation(): boolean {
    return (
      (this.asset.typeId !== AssetTypeMapping.SHIP && !!this.asset.location) ||
      (this.asset.typeId === AssetTypeMapping.SHIP && !!this.asset.shipDetails?.imo)
    );
  }

  isProduct(): boolean {
    return (
      this.asset.typeId === AssetTypeMapping.DEVICE ||
      this.asset.typeId === AssetTypeMapping.MACHINE_SYSTEM ||
      this.asset.typeId === AssetTypeMapping.SOFTWARE
    );
  }

  hasSerialNumber(): boolean {
    return (
      this.asset.typeId === AssetTypeMapping.DEVICE ||
      this.asset.typeId === AssetTypeMapping.MACHINE_SYSTEM
    );
  }

  hasVersionFormatValidation(): boolean {
    return (
      this.asset.typeId === AssetTypeMapping.DEVICE ||
      this.asset.typeId === AssetTypeMapping.MACHINE_SYSTEM
    );
  }

  isTypeShip(): boolean {
    return this.typeSelected === AssetTypeMapping.SHIP;
  }

  hasInventorySync() {
    return this.assetStateService.hasInventorySync(this.asset);
  }

  private reset(): void {
    this.typeSelected = undefined;
    this.resetFirmwareErrors();
    this.updateAsset(this.asset);
  }

  validateVersion(): void {
    const firmwareVersion = this.firmwareVersion.trim();
    this.firmwareVersionValid =
      firmwareVersion === '' ||
      !this.hasVersionFormatValidation() ||
      isVersionValid(firmwareVersion);
    if (this.showVersionErrorIcon && this.firmwareVersionValid) {
      this.showVersionErrorIcon = false;
    }
  }

  onVersionLeaveFocus(): void {
    this.showVersionErrorIcon = !this.firmwareVersionValid;
  }

  typeChanged(event: { originalEvent: Event; value: number }): void {
    if (event.value) {
      this.defaultTitle = this.langService.instant(
        `assetTypes.${assetTypeMappingToString[event.value]}`,
      );
    }
  }

  fallbackToIcon(): void {
    this.profilePicture = { ready: true };
  }

  isVendorSICK(): boolean {
    return !!this.asset.details && validateIsVendorSick(this.asset.details.vendor);
  }

  showAdmins(event: Event): void {
    event.preventDefault();
    AdministratorsModalComponent.open(this.asset.uuid, 'permission-insights.own-permission.r');
  }

  private updateAsset(asset: GetAssetResponse) {
    this.updateAssetAttributes(asset);
    this.updateProfilePicture(asset);
    this.updateProductDetails(asset);
  }

  private updateAssetAttributes(asset: GetAssetResponse) {
    this.asset = asset;
    this.defaultTitle = this.assetService.getTranslatedName(asset).defaultAssetName;
    this.title = asset.customName;

    this.deploymentDateTimestamp = asset.deploymentDate ? asset.deploymentDate : undefined;

    this.deploymentDate = asset.deploymentDate ? new Date(asset.deploymentDate * 1000) : undefined;

    this.currentTypeAsString = assetTypeMappingToString[asset.typeId];
    this.typeSelected = asset.typeId as AssetTypeMapping;
    this.assetTypeChangeable = interchangeableTypeMappings.includes(this.typeSelected);
    this.permissions = asset.permissions;

    if (assetTypesSupportDeploymentDate.includes(this.typeSelected)) {
      this.showDeploymentDateField = true;
      this.deploymentDateLanguageKey = assetTypeMappingToString[this.typeSelected] ?? 'default';
    } else {
      this.showDeploymentDateField = false;
    }

    if (this.isProduct()) {
      this.vendor = asset.details?.vendor || '';
      this.serialNumber = asset.details?.serialNumber || '';
      this.partNumber = asset.details?.partNumber || '';
      this.productName = asset.details?.productName || '';
      this.firmwareVersion = asset.details?.firmwareVersion || '';
      this.firmwareState = asset.details?.firmwareState || 'UNKNOWN';
      this.resetFirmwareErrors();
      this.vendorUpdatable = ['SICK', 'SICK AG'].includes(this.vendor) === false;
      this.serialNumberUpdatable = this.serialNumber === '';
      this.partNumberUpdatable = this.partNumber === '';
      this.productNameUpdatable = this.productName === '';
    } else if (this.isTypeShip()) {
      this.shipImo = asset.shipDetails?.imo || '';
    }
  }

  private resetFirmwareErrors() {
    this.firmwareVersionValid = true;
    this.showVersionErrorIcon = false;
  }

  private updateProfilePicture(asset: GetAssetResponse) {
    this.updating = true;
    this.defaultPictureUrl = this.imageService.getDefaultPictureUrl(asset);

    if (this.profilePictureSubscription) {
      this.profilePictureSubscription.unsubscribe();
      this.profilePictureSubscription = undefined;
    }

    this.profilePictureSubscription = this.profilePictureService
      .getProfilePicture(asset)
      .pipe(
        tap(event => {
          this.logger.info('Received profile picture', event.picture);
          this.profilePicture = event.picture;
          if (asset.profilePicture?.uuid) {
            this.viewerItems.set([
              {
                type: 'custom-image',
                id: asset.profilePicture.uuid,
                filename: asset.images?.find(x => x.uuid === asset.profilePicture!.uuid)?.name,
              },
            ]);
          } else {
            this.viewerItems.set([
              {
                type: 'image',
                id: 'dummy',
                previewUrl: this.completeUrlPipe.transform(event.picture.normal || '', '#'),
                url: this.completeUrlPipe.transform(event.picture.zoomed!, '#'),
              },
            ]);
          }
        }),
        switchMap(() => {
          const uuid = this.asset.profilePicture?.uuid;
          if (!this.profilePicture.zoomed && uuid) {
            return this.imageService.proxyImageObservable(uuid).pipe(
              tap(url => {
                this.profilePicture.zoomed = url;
              }),
            );
          }

          return of(undefined);
        }),
        finalize(() => {
          this.updating = false;
        }),
      )
      .subscribe();
  }

  private updateProductDetails(asset: GetAssetResponse) {
    if (this.productDetailSubscription) {
      this.productDetailSubscription.unsubscribe();
      this.productDetailSubscription = undefined;
    }

    this.resetView();

    if (asset.details && this.isVendorSICK() && asset.typeId !== AssetTypeMapping.SOFTWARE) {
      const partNumber = asset.details.partNumber;
      this.productDetailSubscription = this.productService.getProductDetails(partNumber).subscribe({
        next: productDetails => {
          this.processProductData({
            ...productDetails,
            partNumber: productDetails.partNumber || partNumber,
          });
        },
        error: err => {
          this.logger.warn('Unable to get product details:', err);
          this.shopPath = this.canShowShopLink() ? partNumber : '';
        },
      });
    }
  }

  private resetView() {
    this.phaseOutWarningMsg = '';
    this.successor = undefined;
  }

  private processProductData(productDetails: ProductDetails) {
    if (productDetails.phaseOut === 'InPhaseOut') {
      this.phaseOutWarningMsg = 'assetDetails.phasingOutWarning';
    } else if (productDetails.phaseOut === 'PhasedOut') {
      this.phaseOutWarningMsg = 'assetDetails.phasedOutWarning';
    }
    this.successor = productDetails.successor;
    this.shopPath = this.canShowShopLink() ? productDetails.partNumber : '';
  }

  private createAssetTypeList(): SelectItem<number>[] {
    const supportedAssetTypes: SelectItem<number>[] = [];

    interchangeableTypeMappings.forEach(type => {
      supportedAssetTypes.push({
        label: 'assetTypes.' + assetTypeMappingToString[type],
        value: type,
        icon: 'assets/images/' + assetTypeMappingToString[type] + '.svg',
      });
    });

    return supportedAssetTypes;
  }

  sanitize(url: string): SafeUrl {
    return this.sanitizer.bypassSecurityTrustUrl(url);
  }
}
